import React from 'react';
import { SuperTooltipTemplate } from './types';
import { Modal } from '@design-system/components/modal/modal';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { Col } from '@design-system/components/grid/grid';
import { resolveImagePath } from './utils';
import * as styles from './ChecklistTooltipRenderer.css';

const parseInlineMarkdown = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*|_[^_]+_)/).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });

type ChecklistTooltipRendererProps = {
  template: SuperTooltipTemplate;
  isOpen: boolean;
  onClose: () => void;
  onLinkClicked: (link: string) => void;
};

const MediaSection: React.FC<{ template: SuperTooltipTemplate }> = ({
  template,
}) => {
  if (template.type === 'animated') {
    return (
      <div className={styles.mediaSection}>
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '20px' }}
        >
          <source src={resolveImagePath(template.videoUrl)} type="video/mp4" />
        </video>
      </div>
    );
  }

  if (template.type === 'video') {
    return (
      <div className={styles.mediaSection}>
        <video
          controls
          poster={resolveImagePath(template.videoThumbnailUrl)}
          preload="none"
          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '20px' }}
        >
          <source src={template.videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (
    (template.type === 'basic' || template.type === 'single-image') &&
    template.imageUrl
  ) {
    return (
      <div className={styles.mediaSection}>
        <img
          src={resolveImagePath(template.imageUrl)}
          alt={template.imageAlt || ''}
          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '20px' }}
        />
      </div>
    );
  }

  return null;
};

const isBoldHeading = (tip: string): boolean =>
  tip.startsWith('**') && tip.endsWith('**');

const TipsSection: React.FC<{ tips: string[] }> = ({ tips }) => {
  if (tips.length === 0) return null;

  if (tips.length === 1) {
    return (
      <Typography
        variant="body"
        size="medium"
        fontWeight="normal"
        className={styles.content}
      >
        {parseInlineMarkdown(tips[0])}
      </Typography>
    );
  }

  const groups: Array<{ heading: string | null; items: string[] }> = [];
  for (const tip of tips) {
    if (isBoldHeading(tip)) {
      groups.push({ heading: tip.slice(2, -2), items: [] });
    } else if (groups.length > 0) {
      groups[groups.length - 1].items.push(tip);
    } else {
      if (groups.length === 0) {
        groups.push({ heading: null, items: [] });
      }
      groups[0].items.push(tip);
    }
  }

  return (
    <div className={styles.tipsBox}>
      {groups.map((group, gi) => (
        <div key={gi} className={gi > 0 ? styles.tipsGroup : undefined}>
          {group.heading && (
            <Typography
              variant="body"
              size="medium"
              fontWeight="bold"
              className={styles.tipsHeading}
            >
              {group.heading}
            </Typography>
          )}
          {group.items.length > 0 && (
            <ol className={styles.tipsList}>
              {group.items.map((item, index) => (
                <li key={index}>
                  <Typography variant="body" size="medium" fontWeight="normal">
                    {parseInlineMarkdown(item)}
                  </Typography>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  );
};

const ButtonsSection: React.FC<{
  template: SuperTooltipTemplate;
  onLinkClicked: (link: string) => void;
}> = ({ template, onLinkClicked }) => {
  const hasCta = Boolean(template.ctaLabel && template.ctaLink);
  const hasReadMore = Boolean(template.readMoreText && template.readMoreLink);
  const hasAdditionalLinks = (template.additionalLinks ?? []).length > 0;

  if (!hasCta && !hasReadMore && !hasAdditionalLinks) return null;

  return (
    <Col className={styles.buttonsSection}>
      {template.readMoreText && (
        <Typography
          variant="body"
          size="medium"
          color="N-80"
          fontWeight="normal"
        >
          {template.readMoreText}
        </Typography>
      )}

      {hasCta && (
        <Button
          variant="filled"
          color="neutral"
          onClick={() => onLinkClicked(template.ctaLink)}
        >
          {template.ctaLabel}
        </Button>
      )}

      {hasReadMore && (
        <Button
          variant="filled"
          color="neutral"
          onClick={() => onLinkClicked(template.readMoreLink)}
        >
          {template.readMoreLabel.replace('link:', '')}
        </Button>
      )}

      {template.additionalLinks?.map((link, index) => (
        <React.Fragment key={index}>
          {link.text && (
            <Typography
              variant="body"
              size="medium"
              color="N-80"
              fontWeight="normal"
            >
              {link.text}
            </Typography>
          )}
          <Button
            variant="filled"
            color="neutral"
            onClick={() => onLinkClicked(link.link)}
          >
            {link.label.replace('link:', '')}
          </Button>
        </React.Fragment>
      ))}
    </Col>
  );
};

const ChecklistTooltipRenderer: React.FC<ChecklistTooltipRendererProps> = ({
  template,
  isOpen,
  onClose,
  onLinkClicked,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={template.title}
      centerContent
      modalClassName={styles.modal}
    >
      <div className={styles.wrapper}>
        <Typography
          variant="body"
          size="large"
          fontWeight="normal"
          className={styles.content}
          style={{ whiteSpace: 'pre-line' }}
        >
          {template.content}
        </Typography>

        <MediaSection template={template} />
        {template.footnote && (
          <Typography
            variant="body"
            size="medium"
            fontWeight="normal"
            className={styles.content}
          >
            {parseInlineMarkdown(template.footnote)}
          </Typography>
        )}
        <TipsSection tips={template.tips} />
        <ButtonsSection template={template} onLinkClicked={onLinkClicked} />
      </div>
    </Modal>
  );
};

export default ChecklistTooltipRenderer;
